export type Theme = "light" | "dark" | "system";
const DEFAULT_THEME: Theme = "system";
const THEME_STORAGE_KEY = "UItheme";

/**
 * Applies the given theme to the HTML document by adding/removing CSS classes
 * If the theme is "system", it checks the user's system preference and applies "dark" or "light" accordingly.
 *
 * Save the theme to localStorage
 * @param theme - The theme to apply
 */
export const applyThemeToDocument = (theme: Theme): void => {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  const htmlTag = document.querySelector("html");
  if (htmlTag) {
    htmlTag.classList.remove("light", "dark");
    if (theme === "system") {
      // check on the browser theme preference
      const prefersDarkScheme = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      theme = prefersDarkScheme ? "dark" : "light";
    }
    htmlTag.classList.add(theme);
  }
};

/**
 * Retrieves the theme from localStorage, defaulting to "light" if not set
 * @returns The current theme
 */
export const getThemeFromStorage = (): Theme => {
  const theme = localStorage.getItem(THEME_STORAGE_KEY);
  if (theme) {
    if (["light", "dark", "system"].includes(theme)) {
      return theme as Theme;
    }
  }
  return DEFAULT_THEME;
};

/**
 * Initializes the theme from localStorage and applies it to the document
 * This should be called before React renders to prevent theme flashing
 */
export const initializeTheme = (): void => {
  const theme = getThemeFromStorage();
  applyThemeToDocument(theme);
};
